import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormGroup,
  Row,
  Col,
} from "reactstrap";
import Select from "react-select";
import { reactSelectPortalProps } from "utils/reactSelectStyles";
import FonteBadge from "../../../../components/cotacao/FonteBadge";
import FreteSpotForm from "../../../../components/cotacao/FreteSpotForm";
import moment from "moment";
import { formatCurrency } from "../../../../utils/formatCurrency";
import { formatDate } from "../../../../utils/formatDate";
import { formatBR } from "../../../../utils/formatBR";
import { useCompanies } from "hooks/companies";
import ApiHubBackend from "services/ApiHubBackend";
import api from "services/api";
import { QUOTE_STATUS_OPTIONS, getQuoteStatusLabel } from "../statusOptions";
import { buildVersionLabel, canCreateVersion, canTransition, hasVersions, isNotLowestPrice } from "services/quoteService";
import obterUserEmail from "utils/userEmail";
import obterUserNome from "utils/userNome";
import ColetaPasso from "./ColetaPasso";
import { agregarPropostas, propostaEscolhidaDe, mesmaProposta } from "../agregarPropostas";
import { unidadeOrigemDe } from "../unidadeOrigem"; // SUS-100
import { textoCriadoPor, tituloCriadoPor } from "utils/criadoPor"; // PO1-3488
import { montarOpcoesTiposVeiculo, opcoesPlanas } from "../../NovaCotacaoManual/utils/tiposVeiculo"; // PO1-3480
import { deInputLocal, ehUrgente, paraInputLocal, validarLimites } from "utils/limitesColetaEntrega"; // PO1-3481
import {
  buscarDestinatarioPorCnpj,
  camposDoDocumento,
  cnpjCpfValido,
  formatCnpjCpf,
  montarDestinatario,
  montarEnderecoEntrega,
  soDigitos,
} from "utils/destinatarioCotacao"; // PO1-3487

const selectThemedStyles = {
  control: (base, state) => ({
    ...base,
    background: "var(--everlog-card-surface-field)",
    borderColor: state.isFocused ? "var(--theme-accent)" : "var(--everlog-card-border)",
    boxShadow: state.isFocused ? "0 0 0 0.15rem var(--theme-accent-glow)" : "none",
    fontSize: 13,
    minHeight: 32,
    cursor: "pointer",
  }),
  menu: (base) => ({ ...base, background: "var(--everlog-card-surface-field)" }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    background: isSelected
      ? "var(--theme-accent)"
      : isFocused
      ? "var(--everlog-card-surface-active)"
      : "var(--everlog-card-surface-field)",
    color: isSelected ? "var(--everlog-on-accent-text)" : "var(--everlog-card-title)",
    fontSize: 13,
  }),
  singleValue: (base) => ({ ...base, color: "var(--everlog-card-title)" }),
  input: (base) => ({ ...base, color: "var(--everlog-card-title)" }),
  placeholder: (base) => ({ ...base, color: "var(--everlog-card-muted)" }),
  indicatorSeparator: () => ({ display: "none" }),
};

const CREDENCIAL =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbiIsImlhdCI6MTYxMzM5MDgwNywiZXhwIjoxOTI4NzUwODA3fQ.pYaKQjzeMk1VXqp1YnHIbhSwXMrzfuIWIOawoX9l4ag";

const STATUS_INFO = {
  Pendente: {
    color: "var(--everlog-status-neutral)",
    bg: "color-mix(in srgb, var(--everlog-status-neutral) 12%, transparent)",
    label: "Edição livre",
  },
  "Stand-by": {
    color: "var(--everlog-status-warning)",
    bg: "color-mix(in srgb, var(--everlog-status-warning) 12%, transparent)",
    label: "Edição com auditoria",
  },
  Simulado: {
    color: "var(--everlog-status-info)",
    bg: "color-mix(in srgb, var(--everlog-status-info) 12%, transparent)",
    label: "Dados bloqueados — apenas status",
  },
  Realizado: {
    color: "var(--everlog-status-success)",
    bg: "color-mix(in srgb, var(--everlog-status-success) 12%, transparent)",
    label: "Finalizado — sem alterações",
  },
};

// PO1-2081 — botão flexível de transição no rodapé. O label/alvo deriva do
// status PERSISTIDO da cotação (não do dropdown). Simulado/Realizado não têm
// ação rápida: Realizado é imutável e Simulado só evolui por "Criar nova
// versão" (PO1-2781: Stand-by também oferece "Criar nova versão", ao lado
// de Aprovar — ver canCreateVersion).
const FLEX_ACTION = {
  Pendente:   { target: "Stand-by", label: "Stand-by", color: "warning" },
  "Stand-by": { target: "Simulado", label: "Aprovar",  color: "success" },
};

/* ── helpers ── */
const formatCep = (v) => {
  if (!v) return "—";
  const s = String(v).replace(/\D/g, "").padStart(8, "0");
  return s.replace(/^(\d{5})(\d{3})$/, "$1-$2");
};

const formatCnpj = (v) => {
  if (!v) return "—";
  const s = String(v).replace(/\D/g, "").padStart(14, "0");
  return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const parseBRNum = (v) => {
  if (!v && v !== 0) return null;
  if (typeof v === "number") return v;
  const cleaned = String(v)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
};

const formatBRL = (v) => {
  const n = parseBRNum(v);
  if (n === null) return "";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatNum = (v) => {
  const n = parseBRNum(v);
  if (n === null) return "";
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Instantes reais (solicitadoEm, decididoEm, versionCreatedAt) em hora
// local. formatDate/formatDateTime usam .utc(0) — certo p/ datas "só dia"
// (dataEmbarque), errado p/ timestamps (mostraria +3h / dia seguinte à noite).
const formatInstant = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "—");

const formatPct = (v) => {
  const n = parseBRNum(v);
  if (n === null) return "—";
  return `${n.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
};

const ALCADA_LABELS = {
  solicitada: "solicitada",
  aprovada: "aprovada",
  aprovada_com_excecao: "aprovada com exceção",
  recusada: "recusada",
  sinalizada: "sinalizada",
  cancelada_por_edicao: "cancelada por edição",
};

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, textTransform: "uppercase",
    letterSpacing: "0.5px", color: "var(--everlog-card-title)",
    borderBottom: "1px solid var(--everlog-card-text-softer)", paddingBottom: 6, marginBottom: 14, marginTop: 20,
  }}>
    {children}
  </div>
);

const ReadonlyField = ({ label, value }) => (
  <Col md="4" style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, color: "var(--everlog-card-muted)", marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 13, color: "var(--everlog-card-title)", fontWeight: 500 }}>{value || "—"}</div>
  </Col>
);

const EditField = ({ label, locked, md = "4", children }) => (
  <Col md={md} style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 11, color: locked ? "var(--everlog-card-text-softer)" : "var(--everlog-card-muted)", marginBottom: 4 }}>
      {label}{locked && " 🔒"}
    </div>
    {children}
  </Col>
);

const inputStyle = (locked) => ({
  background: locked ? "var(--everlog-card-surface-muted)" : "var(--everlog-card-surface-field)",
  color: locked ? "var(--everlog-card-text)" : "var(--everlog-card-title)",
  border: "1px solid var(--everlog-card-border)",
  fontSize: 13,
});

/* ── Histórico timeline ── */
const FIELD_LABELS = {
  statusEmbarque: "Status", transportadora: "Transportadora",
  freteSpot: "Frete Spot", valor_total: "Valor Total",
  observacoes: "Observações", valorNF: "Valor NF",
  numEmbarque: "Nº Embarque", cliente: "Cliente",
  pesoBruto: "Peso Bruto", pesoCubado: "Peso Cubado", volume: "Volume",
  versao: "Versão", criadoPor: "Criado por",
  codigoPedido: "Nº do pedido",
  aprovacao: "Alçada", justificativa: "Justificativa", decididoPor: "Decidido por",
  coleta: "Coleta",
};

// PO1-2792 — modo do envio da solicitação de coleta
const COLETA_MODO_LABELS = { automatico: "automático", manual: "manual" };
const coletaModoStr = (modo) => COLETA_MODO_LABELS[modo] || (modo ? String(modo) : "");

const safeStr = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// Valores de histórico que não são texto simples: pessoa ({email, nome}) e
// número de versão (mostrado como v00n). O resto cai no safeStr.
const isPersonLike = (v) =>
  !!v && typeof v === "object" && !Array.isArray(v) && ("email" in v || "nome" in v);

const personStr = (v) => (isPersonLike(v) ? v.nome || v.email || "—" : safeStr(v));

const diffValueStr = (campo, v) => {
  if (v === null || v === undefined) return "—";
  if (campo === "versao" && /^\d+$/.test(String(v))) return buildVersionLabel(v);
  if (campo === "aprovacao" && typeof v === "string") return ALCADA_LABELS[v] || v;
  // statusEmbarque guarda o valor persistido (ex.: "Simulado"); o resto da
  // tela (Status, bloco Versões) já mostra o rótulo amigável ("Aprovado").
  if (campo === "statusEmbarque" && typeof v === "string") return getQuoteStatusLabel(v);
  if (isPersonLike(v)) return personStr(v);
  return safeStr(v);
};

// Complemento textual da linha (quem criou a versão, justificativa...),
// vindo das chaves extras do diff além de anterior/novo.
const diffExtraStr = (campo, diff) => {
  if (!diff || typeof diff !== "object") return "";
  const parts = [];
  // PO1-2799: quem fez a alteração agora aparece uma vez no cabeçalho da
  // entrada (entry.quem) — não repetir aqui por campo.
  if (campo === "aprovacao") {
    if (diff.valorNovo !== undefined && diff.valorReferencia !== undefined)
      parts.push(`${formatBRL(diff.valorNovo)} vs orçado ${formatBRL(diff.valorReferencia)}`);
    if (diff.diferencaPct !== undefined) parts.push(`+${formatPct(diff.diferencaPct)}`);
    if (diff.justificativa) parts.push(`"${diff.justificativa}"`);
  }
  return parts.length ? ` (${parts.join("; ")})` : "";
};

// PO1-2792: um reenvio de coleta registra anterior = novo = "solicitada" e
// ainda assim é um evento real — coleta nunca é filtrada por igualdade.
const hasRealChange = (campo, diff) =>
  diff && typeof diff === "object" && !Array.isArray(diff) &&
  ("anterior" in diff || "novo" in diff) &&
  (campo === "coleta" || String(diff.anterior ?? "") !== String(diff.novo ?? ""));

// Linha do histórico p/ coleta: "solicitada (manual) → transp@x" ou
// "reenviada (automático)". Não usa o par riscado → novo porque o "valor"
// não muda (sempre "solicitada").
const ColetaHistoricoLine = ({ diff }) => {
  const dest = Array.isArray(diff.destinatarios) ? diff.destinatarios.filter(Boolean) : [];
  const modo = coletaModoStr(diff.modo);
  // PO1-3399: cancelamento (novo = "cancelada") e nova solicitação depois dele.
  const cancelada = diff.novo === "cancelada";
  const acao = cancelada
    ? "cancelada"
    : diff.anterior === "solicitada"
      ? "reenviada"
      : diff.anterior === "cancelada"
        ? "solicitada novamente"
        : "solicitada";
  return (
    <div style={{ fontSize: 11, color: "var(--everlog-card-text)", marginBottom: 2 }}>
      <span style={{ color: "var(--everlog-card-muted)" }}>{FIELD_LABELS.coleta}:</span>{" "}
      <span style={{ color: cancelada ? "var(--everlog-status-danger)" : "var(--everlog-status-success)" }}>
        {acao}{!cancelada && modo ? ` (${modo})` : ""}
      </span>
      {dest.length > 0 && (
        <span style={{ color: "var(--everlog-card-muted)" }}>{` → ${dest.join(", ")}`}</span>
      )}
      {cancelada && diff.motivo ? (
        <span style={{ color: "var(--everlog-card-muted)" }}>{` · motivo: ${diff.motivo}`}</span>
      ) : null}
    </div>
  );
};

const HistoricoEntry = ({ entry }) => {
  if (!entry || typeof entry !== "object") return null;
  const camposRaw = entry.campos;
  const campos = camposRaw && typeof camposRaw === "object" && !Array.isArray(camposRaw) ? camposRaw : {};
  const entries = Object.entries(campos).filter(([campo, diff]) => hasRealChange(campo, diff));
  if (entries.length === 0) return null;
  return (
    <div style={{ borderLeft: "2px solid var(--everlog-card-border)", paddingLeft: 12, paddingBottom: 4 }}>
      <div style={{ fontSize: 11, color: "var(--everlog-card-muted)", marginBottom: 4 }}>
        {formatDate(entry.timestamp)} · anterior: <strong style={{ color: "var(--everlog-card-title)" }}>{entry.statusAnterior ? getQuoteStatusLabel(entry.statusAnterior) : "—"}</strong>
        {/* PO1-2799: quem fez a alteração; ausente em histórico anterior à mudança (não retroagido) */}
        {isPersonLike(entry.quem) && (entry.quem.email || entry.quem.nome) && (
          <> · por {personStr(entry.quem)}</>
        )}
      </div>
      {entries.map(([campo, diff]) => campo === "coleta" ? (
        <ColetaHistoricoLine key={campo} diff={diff} />
      ) : (
        <div key={campo} style={{ fontSize: 11, color: "var(--everlog-card-text)", marginBottom: 2 }}>
          <span style={{ color: "var(--everlog-card-muted)" }}>{FIELD_LABELS[campo] || campo}:</span>{" "}
          <span style={{ color: "var(--everlog-status-danger)", textDecoration: "line-through" }}>{diffValueStr(campo, diff.anterior)}</span>
          {" → "}
          <span style={{ color: "var(--everlog-status-success)" }}>{diffValueStr(campo, diff.novo)}</span>
          <span style={{ color: "var(--everlog-card-muted)" }}>{diffExtraStr(campo, diff)}</span>
        </div>
      ))}
    </div>
  );
};

const entryHasRealChange = (entry) => {
  if (!entry || !entry.campos || typeof entry.campos !== "object") return false;
  return Object.entries(entry.campos).some(([campo, diff]) => hasRealChange(campo, diff));
};

const HistoricoTimeline = ({ historico }) => {
  const [expanded, setExpanded] = React.useState(false);

  if (!Array.isArray(historico) || historico.length === 0)
    return <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: 0 }}>Nenhuma alteração registrada.</p>;

  const meaningful = [...historico].reverse().filter(entryHasRealChange);

  if (meaningful.length === 0)
    return <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: 0 }}>Nenhuma alteração registrada.</p>;

  const visible = expanded ? meaningful : meaningful.slice(0, 1);
  const hiddenCount = meaningful.length - 1;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {visible.map((entry, i) => <HistoricoEntry key={i} entry={entry} />)}
      {hiddenCount > 0 && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ background: "none", border: "none", color: "var(--theme-accent)", fontSize: 11, cursor: "pointer", padding: 0, textAlign: "left" }}
        >
          {expanded ? "mostrar menos ▲" : `mostrar mais (${hiddenCount} anterior${hiddenCount > 1 ? "es" : ""}) ▼`}
        </button>
      )}
    </div>
  );
};

/* ── Versões timeline (PO1-2783) ── */
const VersoesTimeline = ({ versoes, loading, activeId, onSelect }) => {
  if (loading)
    return <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: 0 }}>Carregando versões...</p>;
  if (!Array.isArray(versoes) || versoes.length === 0)
    return <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: 0 }}>Nenhuma versão encontrada.</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {versoes.map((v) => {
        const isCurrent = v.isLatestVersion !== false;
        const isActive = String(v._id) === String(activeId);
        const label = v.versionLabel || buildVersionLabel(v.versionNumber);
        const partes = [
          getQuoteStatusLabel(v.statusEmbarque),
          v.transportadora || "—",
          formatBRL(v.valor_total) || "—",
          formatBR(v.codigoPedido) || "BR —",
          formatInstant(v.versionCreatedAt),
        ];
        return (
          <div
            key={v._id}
            role="button"
            tabIndex={0}
            title={isCurrent ? "Versão atual" : "Clique para visualizar esta versão (somente leitura)"}
            onClick={function() { onSelect(isCurrent ? null : v); }}
            onKeyDown={function(e) { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(isCurrent ? null : v); } }}
            style={{
              borderLeft: `2px solid ${isActive ? "var(--theme-accent)" : "var(--everlog-card-border)"}`,
              paddingLeft: 12, paddingBottom: 2, cursor: "pointer",
              fontSize: 11, color: isActive ? "var(--everlog-card-title)" : "var(--everlog-card-text)",
              display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
            }}
          >
            <span className="lc-badge lc-badge-versao">{label}</span>
            {isCurrent && (
              <span style={{ fontSize: 10, fontWeight: 700, color: "var(--theme-accent)", textTransform: "uppercase", letterSpacing: "0.4px" }}>
                atual
              </span>
            )}
            <span style={{ color: "var(--everlog-card-muted)" }}>{partes.join(" · ")}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════════════ */
const EditCotacaoModal = ({
  isOpen,
  onClose,
  emb,
  onSave,
  onCreateVersion,
  onDelete,
  saving,
  isAprovador = false,
  onAprovar,
  onRecusar,
  onSolicitarColeta,
  onCancelarColeta,
  // PO1-3084 (épico PO1-3076): "Leiloar" no rodapé; `leilaoAtivo` = oferta viva
  // desta cotação ({ leilaoId, numero, estado }) → o botão vira "Ver leilão".
  onLeiloar,
  leilaoAtivo,
  // PO1-3488: "frete" = aberto pelo botão "Cotar" da listagem; o modal já
  // rola até a seção "Frete e Transportadora".
  focoInicial = null,
}) => {
  const { empresa } = useCompanies();
  const empresaId = empresa?.value;
  const [form, setForm] = useState({});
  const [cnpjAviso, setCnpjAviso] = useState(null); // PO1-3487
  // PO1-3488: abrir já na seção de frete (botão "Cotar").
  const embIdFoco = emb && emb._id;
  useEffect(() => {
    if (!isOpen || focoInicial !== "frete" || !embIdFoco) return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("cotacao-secao-frete");
      if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start", behavior: "smooth" });
    }, 350);
    return () => clearTimeout(t);
  }, [isOpen, focoInicial, embIdFoco]);
  const [veiculosEdit, setVeiculosEdit] = useState([]);
  const [localProposals, setLocalProposals] = useState([]);
  // PO1-3200: formulário "+ Frete Spot" aberto/fechado.
  const [spotAberto, setSpotAberto] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  // SUS-100: unidades do embarcador, para mostrar de onde a carga sai.
  const [remetentes, setRemetentes] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // PO1-2769 — serviços adicionais (tipo + valor) somados ao frete
  const [servicosEdit, setServicosEdit] = useState([]);
  const [tiposServico, setTiposServico] = useState([]);
  // PO1-2783 — versão antiga sendo visualizada (somente leitura). null = a
  // cotação aberta (emb), que é sempre a versão corrente.
  const [versaoSelecionada, setVersaoSelecionada] = useState(null);
  const [versoes, setVersoes] = useState([]);
  const [versoesLoading, setVersoesLoading] = useState(false);
  // PO1-2713 — recusa inline (textarea obrigatória, mín. 3 caracteres)
  const [recusaOpen, setRecusaOpen] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  // PO1-2792 — reenvio da coleta em dois cliques (sem window.confirm) e
  // estado local de envio p/ o botão mostrar "Enviando...".
  const [coletaEnviando, setColetaEnviando] = useState(false);
  // PO1-2576: canal (api|email), conector, data padrão e se cancela.
  const [coletaOpcoes, setColetaOpcoes] = useState(null);
  const [coletaOpcoesLoading, setColetaOpcoesLoading] = useState(false);
  // PO1-3399 — cancelamento da coleta: formulário inline com motivo obrigatório.


  const activeDoc = versaoSelecionada || emb;
  // PO1-3303 (H1, decisão 6A): cotação feita "por caixas" mostra a lista só
  // para leitura e trava peso e medidas (foram derivados dela). Para mudar as
  // caixas, faz-se uma cotação nova.
  const caixasDoc = activeDoc && Array.isArray(activeDoc.caixas) ? activeDoc.caixas : [];
  const temCaixas = caixasDoc.length > 0;
  const unidadeOrigem = activeDoc ? unidadeOrigemDe(remetentes, activeDoc.cnpjRemetente) : null; // SUS-100
  const embId = emb ? emb._id : null;
  const embHasVersions = hasVersions(emb);
  const embPendente = !!(emb && emb.aprovacaoPendente);

  useEffect(() => {
    if (!empresaId) return;
    // PO1-3480: catálogo inteiro, com os que têm tabela primeiro (igual à Nova Cotação).
    Promise.all([
      api.get("/frete/tabelas/ativas/tipos-veiculo", { params: { idEmbarcador: empresaId } }),
      api.get("/frete/tipos-veiculo", { params: { idEmbarcador: empresaId } }).catch(() => ({ data: [] })),
    ])
      .then(([res, cat]) => {
        setVehicleTypes(
          montarOpcoesTiposVeiculo(
            Array.isArray(res.data) ? res.data : [],
            Array.isArray(cat.data) ? cat.data : []
          )
        );
      })
      .catch(() => {});
  }, [empresaId]); // eslint-disable-line

  // SUS-100: mesma lista de remetentes da Nova Cotação; casada pelo CNPJ.
  useEffect(() => {
    if (!empresaId) return;
    api.get(`/embarcadores/${empresaId}`)
      .then((res) => setRemetentes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRemetentes([]));
  }, [empresaId]);

  // PO1-2769 — tipos de serviço (mesma rota de leitura usada em NovaTabelaFrete)
  useEffect(() => {
    api.get("/frete/tipos-servico")
      .then((res) => {
        const raw = Array.isArray(res.data) ? res.data : [];
        setTiposServico(
          raw
            .filter((t) => t && (t.id ?? t.Id) != null)
            .map((t) => ({
              value: t.id ?? t.Id,
              label: (t.nome ?? t.Nome) || String(t.id ?? t.Id),
            }))
        );
      })
      .catch(() => {});
  }, []);

  // Ao trocar de cotação, volta para a versão corrente e busca o grupo de
  // versões (só quando há mais de uma). Reexecuta quando o status/pendência
  // muda p/ a timeline refletir aprovar/recusar sem fechar o modal.
  useEffect(() => {
    setVersaoSelecionada(null);
    setRecusaOpen(false);
    setJustificativa("");
  }, [embId, embPendente]);

  useEffect(() => {
    if (!embId || !embHasVersions) {
      setVersoes([]);
      return undefined;
    }
    let cancelled = false;
    setVersoesLoading(true);
    api.get(`/embarcadores/embarques/${embId}/versoes`)
      .then((res) => {
        if (cancelled) return;
        const lista = res.data && Array.isArray(res.data.versoes) ? res.data.versoes : [];
        setVersoes(lista);
        setVersoesLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setVersoes([]);
        setVersoesLoading(false);
      });
    return () => { cancelled = true; };
  }, [embId, embHasVersions, embPendente, emb && emb.statusEmbarque]); // eslint-disable-line

  useEffect(() => {
    const src = activeDoc;
    if (!src) return;
    setForm({
      statusEmbarque: src.statusEmbarque || "Pendente",
      numEmbarque:    src.numEmbarque    || "",
      codigoPedido:   src.codigoPedido   || "",
      cliente:        src.cliente        || "",
      transportadora: src.transportadora || "",
      transportadoraId: src.transportadoraId != null ? src.transportadoraId : null,
      valorNF:        formatBRL(src.valorNF),
      pesoBruto:      formatNum(src.pesoBruto),
      pesoCubado:     formatNum(src.pesoCubado),
      volume:         formatNum(src.volume),
      densidade:      formatNum(src.densidade),
      largura:        formatNum(src.largura),
      altura:         formatNum(src.altura),
      comprimento:    formatNum(src.comprimento),
      valor_total:    formatBRL(src.valor_total),
      freteSpot:      formatBRL(src.freteSpot),
      observacoes:    src.observacoes    || "",
      // PO1-3481: limites de coleta/entrega no formato do input.
      coletarAte:     paraInputLocal(src.coletarAte),
      // PO1-3487: destinatário da NF e complemento do local de entrega.
      ...camposDoDocumento(src),
      entregarAte:    paraInputLocal(src.entregarAte),
      // PO1-2570 (decisão 5A): conector/serviço/protocolo da proposta escolhida.
      propostaEscolhida: src.propostaEscolhida || null,
    });

    // Inicializa mix de veículos editável
    // Suporte ao schema legado (campos flat: tipoVeiculo, tipoVeiculoId, quantidadeVeiculos)
    let mix;
    if (Array.isArray(src.veiculosMix) && src.veiculosMix.length > 0) {
      mix = src.veiculosMix.map(function(v) {
        return {
          tipoVeiculo:   v.tipoVeiculo   || "",
          tipoVeiculoId: v.tipoVeiculoId || null,
          quantidade:    v.quantidade    != null ? String(v.quantidade) : "1",
          peso:          v.peso          != null ? String(v.peso) : "",
          freteSimulado: v.freteSimulado != null ? v.freteSimulado : null,
        };
      });
    } else if (src.tipoVeiculo || src.tipoVeiculoId) {
      // Schema legado — converte campos flat para o formato mix
      mix = [{
        tipoVeiculo:   src.tipoVeiculo   || "",
        tipoVeiculoId: src.tipoVeiculoId || null,
        quantidade:    src.quantidadeVeiculos != null ? String(src.quantidadeVeiculos) : "1",
        peso:          src.pesoBruto     != null ? String(src.pesoBruto) : "",
        freteSimulado: null,
      }];
    } else {
      mix = [];
    }
    setVeiculosEdit(mix);

    // PO1-2769 — inicializa serviços da cotação
    const servicosSrc = Array.isArray(src.servicos) ? src.servicos.filter(Boolean) : [];
    setServicosEdit(servicosSrc.map(function(s) {
      return {
        idTipoServico: s.idTipoServico != null ? s.idTipoServico : null,
        tipoServico:   s.tipoServico || "",
        valor:         s.valor != null ? formatBRL(s.valor) : "",
      };
    }));

    // Inicializa propostas
    const cotacoes = Array.isArray(src.cotacoes) ? src.cotacoes.filter(Boolean) : [];
    const sorted = cotacoes.slice().sort(function(a, b) { return (a.valor || 0) - (b.valor || 0); });
    setLocalProposals(sorted);
    setSpotAberto(false);
    setConfirmDelete(false);
  }, [activeDoc]);

  // PO1-2576: ao abrir uma cotação Aprovada (ou com coleta), pergunta ao
  // backend por qual canal a coleta sai (API quando o vínculo tem cap_coleta).
  // Fica ANTES do `if (!emb) return null`: hook depois de um return condicional
  // muda a ordem dos hooks quando o modal abre e derruba a tela (QA 11/10/2026).
  const opcoesDocId = activeDoc && activeDoc._id ? activeDoc._id : null;
  const opcoesColeta = activeDoc && activeDoc.coleta && typeof activeDoc.coleta === "object" ? activeDoc.coleta : null;
  const opcoesStatus = activeDoc ? activeDoc.statusEmbarque : null;
  const opcoesPodeSolicitar =
    opcoesStatus === "Simulado" && !!activeDoc && activeDoc.isLatestVersion !== false && !!onSolicitarColeta;
  useEffect(() => {
    if (!isOpen || !embId || !(opcoesColeta || opcoesPodeSolicitar)) {
      setColetaOpcoes(null);
      return undefined;
    }
    let ativo = true;
    setColetaOpcoesLoading(true);
    api.get(`/embarcadores/embarques/${embId}/coleta/opcoes`)
      .then((r) => { if (ativo) setColetaOpcoes(r.data || null); })
      .catch(() => { if (ativo) setColetaOpcoes(null); })
      .finally(() => { if (ativo) setColetaOpcoesLoading(false); });
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, embId, opcoesDocId, opcoesStatus, opcoesColeta && opcoesColeta.solicitadaEm, opcoesColeta && opcoesColeta.canceladaEm, opcoesPodeSolicitar]);

  if (!emb) return null;

  // readOnly: versão antiga (isLatestVersion === false) é somente leitura
  // total — nenhum input, nenhuma ação. Tudo abaixo deriva de activeDoc.
  const readOnly = activeDoc.isLatestVersion === false;
  const currentStatus = activeDoc.statusEmbarque;
  const info = STATUS_INFO[currentStatus] || STATUS_INFO["Pendente"];
  const isRealizado = currentStatus === "Realizado";
  const isSimulado  = currentStatus === "Simulado";
  const dataLocked  = isSimulado || isRealizado || readOnly;
  // PO1-3303 (6A): com caixas, peso e medidas são derivados e ficam travados.
  const cargaLocked = dataLocked || temCaixas;
  const statusLocked = isRealizado || readOnly;
  const activeVersionLabel = activeDoc.versionLabel || buildVersionLabel(activeDoc.versionNumber);
  const aprovacao = activeDoc.aprovacao && typeof activeDoc.aprovacao === "object" ? activeDoc.aprovacao : null;
  const aprovacaoPendente = !!activeDoc.aprovacaoPendente || !!(aprovacao && aprovacao.pendente);
  const aprovacaoRecusada = !aprovacaoPendente && !!aprovacao && aprovacao.decisao === "recusada";
  const naoMenorPreco = isNotLowestPrice(activeDoc);
  const justificativaOk = justificativa.trim().length >= 3;
  // PO1-2792 — coleta: a faixa aparece sempre que já foi solicitada (inclusive
  // em Realizado e em versão histórica); o botão só em Aprovado, na versão
  // corrente.
  const coleta = activeDoc.coleta && typeof activeDoc.coleta === "object" ? activeDoc.coleta : null;
  const canSolicitarColeta = isSimulado && !readOnly && !!onSolicitarColeta;
  const coletaBusy = saving || coletaEnviando;
  // PO1-3399 — coleta cancelada continua na faixa (com motivo) e libera uma
  // nova solicitação; cancelar só na versão corrente e antes de Realizado.
  const coletaAtiva = !!(coleta && coleta.solicitadaEm && !coleta.canceladaEm);
  const canCancelarColeta = coletaAtiva && !readOnly && !isRealizado && !!onCancelarColeta;


  // Visualizar uma versão antiga: usa o doc da lista na hora (responsivo) e
  // troca pelo doc completo (com historico) assim que chega.
  const handleSelectVersao = (doc) => {
    if (!doc || doc.isLatestVersion !== false) {
      setVersaoSelecionada(null);
      return;
    }
    setVersaoSelecionada(doc);
    api.get(`/embarcadores/embarques/by-id/${doc._id}`)
      .then((res) => {
        const full = res.data;
        if (!full || !full._id) return;
        setVersaoSelecionada((cur) => (cur && String(cur._id) === String(full._id) ? full : cur));
      })
      .catch(() => {});
  };

  // PO1-2576: opcoes = { dataColeta, canal } (data da coleta pela API e
  // "Enviar por e-mail" ao lado da falha).
  const handleSolicitarColeta = async (opcoes) => {
    if (!canSolicitarColeta || coletaBusy) return;
    setColetaEnviando(true);
    try {
      await onSolicitarColeta(emb._id, opcoes || {});
    } finally {
      setColetaEnviando(false);
    }
  };

  const handleCancelarColeta = async (motivo) => {
    if (!canCancelarColeta || coletaBusy) return false;
    setColetaEnviando(true);
    try {
      return await onCancelarColeta(emb._id, motivo);
    } finally {
      setColetaEnviando(false);
    }
  };

  // PO1-3487: consulta do destinatário pelo CNPJ (só preenche o destinatário;
  // o local de entrega da cotação não muda na edição).
  const buscarCnpjDestinatario = async () => {
    const digits = soDigitos(form.destCnpjCpf);
    setCnpjAviso(null);
    if (digits.length !== 14) {
      if (digits && digits.length !== 11) setCnpjAviso("Informe 14 dígitos (CNPJ) ou 11 (CPF).");
      return;
    }
    try {
      const d = await buscarDestinatarioPorCnpj(digits);
      if (!d) return;
      setForm((prev) => ({
        ...prev,
        cliente: d.razaoSocial || prev.cliente,
        destLogradouro: d.logradouro,
        destNumero: d.numero,
        destComplemento: d.complemento,
        destBairro: d.bairro,
        destCep: d.cep,
        destCidade: d.cidade,
        destUf: d.uf,
      }));
    } catch (err) {
      setCnpjAviso("CNPJ não encontrado na Receita. Preencha os dados à mão.");
    }
  };

  // React 16 event pooling: captura o valor antes do updater funcional
  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const blurCurrency = (field) => () => {
    const formatted = formatBRL(form[field]);
    if (formatted) setForm((prev) => ({ ...prev, [field]: formatted }));
  };
  const blurNum = (field) => () => {
    const formatted = formatNum(form[field]);
    if (formatted) setForm((prev) => ({ ...prev, [field]: formatted }));
  };

  // Atualiza campo de um veículo no mix
  const setVeiculoField = (idx, field) => (e) => {
    const value = e.target.value;
    setVeiculosEdit(function(prev) {
      return prev.map(function(v, i) {
        return i === idx ? Object.assign({}, v, { [field]: value }) : v;
      });
    });
  };

  const handleVeiculoTypeChange = (idx, opt) => {
    setVeiculosEdit(function(prev) {
      return prev.map(function(v, i) {
        return i === idx
          ? Object.assign({}, v, { tipoVeiculo: opt ? opt.label : "", tipoVeiculoId: opt ? opt.value : null })
          : v;
      });
    });
  };

  const handleAddVeiculo = () => {
    setVeiculosEdit(function(prev) {
      return [...prev, { tipoVeiculo: "", tipoVeiculoId: null, quantidade: "1", peso: "", freteSimulado: null }];
    });
  };

  const handleRemoveVeiculo = (idx) => {
    setVeiculosEdit(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  };

  // PO1-2769 — linhas de serviço (tipo + valor)
  const handleAddServico = () => {
    setServicosEdit(function(prev) {
      return [...prev, { idTipoServico: null, tipoServico: "", valor: "" }];
    });
  };

  const handleRemoveServico = (idx) => {
    setServicosEdit(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
  };

  const handleServicoTipoChange = (idx, opt) => {
    setServicosEdit(function(prev) {
      return prev.map(function(s, i) {
        return i === idx
          ? Object.assign({}, s, { idTipoServico: opt ? opt.value : null, tipoServico: opt ? opt.label : "" })
          : s;
      });
    });
  };

  const setServicoValor = (idx) => (e) => {
    const value = e.target.value;
    setServicosEdit(function(prev) {
      return prev.map(function(s, i) {
        return i === idx ? Object.assign({}, s, { valor: value }) : s;
      });
    });
  };

  const blurServicoValor = (idx) => () => {
    setServicosEdit(function(prev) {
      return prev.map(function(s, i) {
        if (i !== idx) return s;
        const formatted = formatBRL(s.valor);
        return formatted ? Object.assign({}, s, { valor: formatted }) : s;
      });
    });
  };

  // PO1-2769 — total reativo: frete (valor_total) + soma dos serviços
  const somaServicos = servicosEdit.reduce(function(acc, s) {
    return acc + (parseBRNum(s.valor) || 0);
  }, 0);
  const totalComServicos = (parseBRNum(form.valor_total) || 0) + somaServicos;

  // Seleciona uma proposta (transportadora + valor)
  const handleSelectProposal = function(proposal) {
    setForm(function(prev) {
      return Object.assign({}, prev, {
        transportadora: proposal.transportadora || "",
        transportadoraId:
          proposal.idTransportadora != null ? proposal.idTransportadora : null,
        valor_total: formatBRL(proposal.valor),
        // PO1-2570 (decisão 5A): conector/serviço/protocolo viajam com a escolha.
        propostaEscolhida: propostaEscolhidaDe(proposal),
      });
    });
  };

  // PO1-3200: "+ Frete Spot". Um spot por transportadora (incluir de novo
  // substitui o valor); entra na lista por valor e já fica selecionado.
  const handleAddSpot = function(proposta) {
    setLocalProposals(function(prev) {
      return prev
        .filter(function(p) {
          return !(p.fonte === "spot" && p.idTransportadora === proposta.idTransportadora);
        })
        .concat([proposta])
        .sort(function(a, b) { return (a.valor || 0) - (b.valor || 0); });
    });
    handleSelectProposal(proposta);
    setSpotAberto(false);
  };

  const handleRemoveSpot = function(proposta) {
    setLocalProposals(function(prev) {
      return prev.filter(function(p) {
        return !(p.fonte === "spot" && p.idTransportadora === proposta.idTransportadora);
      });
    });
  };

  // Re-simula fretes com base nos dados atuais do form
  const handleSimular = async function() {
    const cepDigits = String(emb.cepDestino || "").replace(/\D/g, "");
    if (!cepDigits) {
      alert("CEP de destino não encontrado. Preencha antes de simular.");
      return;
    }

    const idEmbarcador = empresaId || null;
    if (!idEmbarcador) {
      alert("Embarcador não identificado.");
      return;
    }

    setSimulating(true);
    try {
      const valorNFNumber = parseBRNum(form.valorNF) || 0;

      // Usa o mix de veículos editado, ou cria um embarque simples se não houver mix
      var embarquesPayload;
      if (veiculosEdit.length > 0) {
        embarquesPayload = veiculosEdit.map(function(v, i) {
          return {
            numero: String(emb.numEmbarque || "SN") + "-" + (i + 1),
            data: new Date().toISOString(),
            CNPJEmitente: String(emb.cnpjRemetente || "").replace(/\D/g, ""),
            CEPDestinatario: cepDigits,
            // PO1-3303: a re-simulação leva as caixas; o backend deriva peso e m³.
            caixas: temCaixas ? caixasDoc : undefined,
            peso: parseBRNum(v.peso) || parseBRNum(form.pesoBruto) || 0,
            // PO1-2903 (H2): o que cuba e o volume em m3.
            volume: parseBRNum(form.volume) || undefined,
            densidade: parseBRNum(form.densidade) || undefined,
            valor: i === 0 ? valorNFNumber : 0,
            idTipoVeiculo: v.tipoVeiculoId || null,
            quantidadeVeiculos: Number(v.quantidade) || 1,
          };
        });
      } else {
        embarquesPayload = [{
          numero: String(emb.numEmbarque || "SN") + "-1",
          data: new Date().toISOString(),
          CNPJEmitente: String(emb.cnpjRemetente || "").replace(/\D/g, ""),
          CEPDestinatario: cepDigits,
          caixas: temCaixas ? caixasDoc : undefined, // PO1-3303
          peso: parseBRNum(form.pesoBruto) || 0,
          volume: parseBRNum(form.volume) || undefined,
          densidade: parseBRNum(form.densidade) || undefined,
          valor: valorNFNumber,
          idTipoVeiculo: null,
          quantidadeVeiculos: 1,
        }];
      }

      const response = await ApiHubBackend.post("/frete/consulta-frete", {
        credencial: CREDENCIAL,
        codigoModal: 1,
        quantidadeRetornos: 5,
        idEmbarcador: idEmbarcador,
        idEmbarcadorRemetente: idEmbarcador,
        embarques: embarquesPayload,
      });

      const results = Array.isArray(response.data) ? response.data : [response.data];

      // Agrega cotações por transportadora|servico (PO1-2570, decisão 5A): a
      // proposta de API traz conectorKey/servico/protocolo e a soma continua
      // sendo ENTRE OS EMBARQUES DO MIX (um `result` por veículo).
      var totals = agregarPropostas(results);

      // PO1-3200: os spots lançados à mão sobrevivem à re-simulação.
      var spotsAtuais = localProposals.filter(function(p) { return p.fonte === "spot"; });
      var proposals = totals
        .concat(spotsAtuais)
        .sort(function(a, b) { return a.valor - b.valor; });
      setLocalProposals(proposals);

      // Atualiza freteSimulado no mix de veículos
      if (veiculosEdit.length > 0) {
        setVeiculosEdit(function(prev) {
          return prev.map(function(v, i) {
            var numero = String(emb.numEmbarque || "SN") + "-" + (i + 1);
            var result = results.find(function(r) { return r && String(r.numero) === numero; });
            var fretes = result && Array.isArray(result.freteCalculado) ? result.freteCalculado : [];
            var melhorFrete = fretes.length > 0
              ? fretes.slice().sort(function(a, b) { return (a.custoFrete || 0) - (b.custoFrete || 0); })[0].custoFrete
              : null;
            return Object.assign({}, v, { freteSimulado: melhorFrete });
          });
        });
      }

      // Auto-seleciona o mais barato
      if (proposals.length > 0) {
        var cheapest = proposals[0];
        setForm(function(prev) {
          return Object.assign({}, prev, {
            transportadora: cheapest.transportadora,
            transportadoraId:
              cheapest.idTransportadora != null ? cheapest.idTransportadora : null,
            valor_total: formatBRL(cheapest.valor),
            propostaEscolhida: propostaEscolhidaDe(cheapest),
          });
        });
      }
    } catch (err) {
      console.error("Erro ao simular fretes:", err);
      alert("Falha ao simular fretes. Verifique o console para detalhes.");
    } finally {
      setSimulating(false);
    }
  };

  const buildPayload = (statusOverride) => {
    const payload = { statusEmbarque: statusOverride || form.statusEmbarque };
    // PO1-2082: usuário logado p/ CC + reply-to no email de aprovação. Não é
    // persistido — o backend só usa na transição → Simulado. userNome vai
    // junto p/ exibir "Aprovado por: Nome (email)" no corpo do email.
    payload.userEmail = obterUserEmail();
    payload.userNome = obterUserNome();
    if (!dataLocked) {
      payload.numEmbarque    = form.numEmbarque;
      // PO1-2782: BR / Nº do pedido (opcional, máx. 20, sem formato). Vazio
      // vai como "" e o backend normaliza para null.
      payload.codigoPedido   = (form.codigoPedido || "").trim();
      payload.cliente        = form.cliente;
      // PO1-3487: "cliente" é a razão social do destinatário.
      payload.destinatario   = montarDestinatario({ ...form, destinatario: form.cliente });
      payload.enderecoEntrega = montarEnderecoEntrega(form);
      payload.transportadora = form.transportadora;
      payload.transportadoraId = form.transportadoraId != null ? form.transportadoraId : null;
      // PO1-2570 (decisão 5A): null limpa (tabela/spot), objeto grava.
      payload.propostaEscolhida = form.propostaEscolhida || null;
      payload.valorNF        = parseBRNum(form.valorNF);
      payload.pesoBruto      = parseBRNum(form.pesoBruto);
      payload.pesoCubado     = parseBRNum(form.pesoCubado);
      payload.volume         = parseBRNum(form.volume);
      payload.densidade      = parseBRNum(form.densidade);
      payload.largura        = parseBRNum(form.largura);
      payload.altura         = parseBRNum(form.altura);
      payload.comprimento    = parseBRNum(form.comprimento);
      payload.valor_total    = parseBRNum(form.valor_total);
      payload.freteSpot      = parseBRNum(form.freteSpot);
      payload.observacoes    = form.observacoes;
      payload.coletarAte     = deInputLocal(form.coletarAte); // PO1-3481
      payload.entregarAte    = deInputLocal(form.entregarAte);
      payload.veiculosMix    = veiculosEdit.map(function(v) {
        return {
          tipoVeiculo:   v.tipoVeiculo,
          tipoVeiculoId: v.tipoVeiculoId,
          quantidade:    Number(v.quantidade) || 1,
          peso:          parseBRNum(v.peso),
          freteSimulado: v.freteSimulado,
        };
      });
      payload.cotacoes = localProposals;
      // PO1-2769 — serviços adicionais; valor_total segue sendo o frete
      // (base) e o total exibido (frete + serviços) é derivado na tela.
      payload.servicos = servicosEdit
        .filter(function(s) { return s.idTipoServico != null || (parseBRNum(s.valor) || 0) > 0; })
        .map(function(s) {
          return {
            idTipoServico: s.idTipoServico,
            tipoServico:   s.tipoServico || null,
            valor:         parseBRNum(s.valor) || 0,
          };
        });
    }
    return payload;
  };

  // PO1-2769 — linha de serviço incompleta = tipo sem valor, ou valor sem
  // tipo selecionado. Só se aplica quando servicos entra no payload.
  const hasServicoIncompleto = () => {
    return servicosEdit.some(function(s) {
      var temTipo = s.idTipoServico != null;
      var temValor = (parseBRNum(s.valor) || 0) > 0;
      return temTipo !== temValor;
    });
  };

  // PO1-3481: limite que já estava salvo e venceu não trava a edição de
  // outros campos; só o valor alterado precisa estar no futuro.
  const limitesErros = (function () {
    if (dataLocked || !activeDoc) return {};
    const erros = validarLimites({ coletarAte: form.coletarAte, entregarAte: form.entregarAte });
    if (erros.coletarAte && form.coletarAte === paraInputLocal(activeDoc.coletarAte) && /já passou/.test(erros.coletarAte)) delete erros.coletarAte;
    if (erros.entregarAte && form.entregarAte === paraInputLocal(activeDoc.entregarAte) && /já passou/.test(erros.entregarAte)) delete erros.entregarAte;
    return erros;
  })();

  const handleSave = () => {
    if (!dataLocked && hasServicoIncompleto()) {
      alert("Há um serviço com tipo ou valor não preenchido. Complete ou remova a linha antes de salvar.");
      return;
    }
    if (Object.keys(limitesErros).length) return;
    if (!dataLocked && !cnpjCpfValido(form.destCnpjCpf)) return; // PO1-3487
    onSave(emb._id, buildPayload());
  };

  // PO1-2081 — transição rápida pelo botão do rodapé. Origem = status
  // PERSISTIDO (currentStatus), não o dropdown. Salva pelo mesmo onSave, então
  // em Pendente/Stand-by os dados editados seguem junto (comportamento atual).
  const flexAction = FLEX_ACTION[currentStatus];
  const canFlex =
    !readOnly &&
    !!flexAction &&
    canTransition(currentStatus, flexAction.target) &&
    !(aprovacaoPendente && !isAprovador);

  const handleFlexAction = () => {
    if (!canFlex) return;
    if (!dataLocked && hasServicoIncompleto()) {
      alert("Há um serviço com tipo ou valor não preenchido. Complete ou remova a linha antes de salvar.");
      return;
    }
    onSave(emb._id, buildPayload(flexAction.target));
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" modalClassName="modal-align-top" contentClassName="lc-edit-modal" fade={false}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "var(--everlog-card-hero-bg)",
        borderBottom: "1px solid var(--everlog-card-border)",
        padding: "16px 20px",
      }}>
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15, display: "inline-flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <span>Cotação {activeDoc.numEmbarque} — {readOnly ? "Visualizar" : "Editar"}</span>
          {/* PO1-3488: quem criou, no topo (antes só na linha do tempo, no fim). */}
          {activeDoc.criadoPor && textoCriadoPor(activeDoc.criadoPor) ? (
            <span
              id="cotacao-criado-por"
              title={tituloCriadoPor(activeDoc.criadoPor)}
              style={{ flexBasis: "100%", fontSize: 12, fontWeight: 400, color: "var(--everlog-card-muted)" }}
            >
              {textoCriadoPor(activeDoc.criadoPor)}
            </span>
          ) : null}
          {/* PO1-2863: nº sequencial (somente leitura; não entra no formulário) */}
          {activeDoc.numeroCotacao != null && (
            <span className="lc-badge lc-badge-cotacao" title="Nº da cotação">
              Nº {activeDoc.numeroCotacao}
            </span>
          )}
          {hasVersions(activeDoc) && (
            <span className="lc-badge lc-badge-versao" title="Versão da cotação">
              {activeVersionLabel}
            </span>
          )}
          {naoMenorPreco && (
            <span className="lc-badge lc-badge-preco" title="A transportadora selecionada não tem a menor proposta">
              Não é o menor preço
            </span>
          )}
          {activeDoc.aproximado && (
            <span
              title="Frete aproximado (estimativa)"
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: 10,
                background: "color-mix(in srgb, var(--everlog-status-warning) 18%, transparent)",
                color: "var(--everlog-status-warning)",
                border: "1px solid color-mix(in srgb, var(--everlog-status-warning) 40%, transparent)",
              }}
            >
              Aproximado
            </span>
          )}
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--everlog-card-title)",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>

        {/* Banner de status */}
        <div style={{
          background: info.bg, borderLeft: `3px solid ${info.color}`,
          borderRadius: 4, padding: "8px 12px", marginBottom: 20,
          fontSize: 12, color: info.color, display: "flex", alignItems: "center", gap: 8,
        }}>
          <span style={{ fontWeight: 700 }}>{getQuoteStatusLabel(currentStatus)}</span>
          <span style={{ opacity: 0.8 }}>—</span>
          <span>{info.label}</span>
          {dataLocked && (
            <span
              role="img"
              aria-label="bloqueado"
              style={{ marginLeft: "auto", fontSize: 16 }}
            >
              🔒
            </span>
          )}
        </div>

        {/* ── Alçada (PO1-2713) ── */}
        {aprovacaoPendente && aprovacao && (
          <div style={{
            background: "color-mix(in srgb, var(--everlog-status-warning) 12%, transparent)",
            borderLeft: "3px solid var(--everlog-status-warning)",
            borderRadius: 4, padding: "10px 12px", marginBottom: 20,
            fontSize: 12, color: "var(--everlog-card-text)", lineHeight: 1.5,
          }}>
            <div style={{ color: "var(--everlog-status-warning)", fontWeight: 700, marginBottom: 2 }}>
              Aguardando aprovação
            </div>
            <div>
              <strong>{formatBRL(aprovacao.valorNovo) || "—"}</strong> acima do orçado{" "}
              <strong>{formatBRL(aprovacao.valorReferencia) || "—"}</strong>
              {aprovacao.versaoReferencia ? ` (${buildVersionLabel(aprovacao.versaoReferencia)})` : ""};
              {" "}diferença <strong>{formatBRL(aprovacao.diferenca) || "—"}</strong> ({formatPct(aprovacao.diferencaPct)}),
              {" "}tolerância {formatPct(aprovacao.toleranciaPct)}.
            </div>
            <div style={{ color: "var(--everlog-card-muted)" }}>
              Solicitado por {personStr(aprovacao.solicitadoPor)} em {formatInstant(aprovacao.solicitadoEm)}
            </div>
            {!isAprovador && (
              <div style={{ color: "var(--everlog-card-muted)", marginTop: 4 }}>
                A aprovação depende de um aprovador configurado para este embarcador.
              </div>
            )}
            {isAprovador && !readOnly && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
                {!recusaOpen ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Button
                      color="success" size="sm"
                      onClick={function() { if (onAprovar) onAprovar(emb._id); }}
                      disabled={saving}
                      style={{ fontSize: 12, padding: "5px 14px" }}
                    >
                      {saving ? "Processando..." : "Aprovar"}
                    </Button>
                    <Button
                      color="danger" size="sm" outline
                      onClick={function() { setRecusaOpen(true); }}
                      disabled={saving}
                      style={{ fontSize: 12, padding: "5px 14px" }}
                    >
                      Recusar
                    </Button>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ fontSize: 11, color: "var(--everlog-card-muted)" }}>
                      Justificativa da recusa (obrigatória, mínimo 3 caracteres)
                    </div>
                    <Input
                      type="textarea" rows={2} value={justificativa}
                      onChange={function(e) { setJustificativa(e.target.value); }}
                      placeholder="Ex.: negociar novamente com a transportadora"
                      style={{ ...inputStyle(false), resize: "vertical" }}
                    />
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Button
                        color="danger" size="sm"
                        onClick={function() { if (onRecusar && justificativaOk) onRecusar(emb._id, justificativa.trim()); }}
                        disabled={saving || !justificativaOk}
                        style={{ fontSize: 12, padding: "5px 14px" }}
                      >
                        {saving ? "Processando..." : "Confirmar recusa"}
                      </Button>
                      <Button
                        className="lc-btn-cancel" size="sm"
                        onClick={function() { setRecusaOpen(false); setJustificativa(""); }}
                        disabled={saving}
                        style={{ fontSize: 12, padding: "5px 14px" }}
                      >
                        Cancelar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {aprovacaoRecusada && (
          <div style={{
            background: "color-mix(in srgb, var(--everlog-status-neutral) 12%, transparent)",
            borderLeft: "3px solid var(--everlog-status-neutral)",
            borderRadius: 4, padding: "8px 12px", marginBottom: 20,
            fontSize: 12, color: "var(--everlog-card-text)", lineHeight: 1.5,
          }}>
            <strong>Recusada</strong> por {personStr(aprovacao.decididoPor)} em {formatInstant(aprovacao.decididoEm)}
            {aprovacao.justificativa ? `: ${aprovacao.justificativa}` : ""}
          </div>
        )}

        {/* ── Coleta (PO1-2792 e-mail · PO1-2576 API da transportadora) ── */}
        {(coleta || canSolicitarColeta) && (
          <ColetaPasso
            coleta={coleta}
            opcoes={coletaOpcoes}
            carregandoOpcoes={coletaOpcoesLoading}
            canSolicitar={canSolicitarColeta}
            canCancelar={canCancelarColeta}
            busy={coletaBusy}
            enviando={coletaEnviando}
            onSolicitar={handleSolicitarColeta}
            onCancelar={handleCancelarColeta}
            inputStyle={inputStyle}
            transportadoraNome={emb.transportadora}
          />
        )}

        {readOnly && (
          <div style={{
            background: "color-mix(in srgb, var(--everlog-status-neutral) 12%, transparent)",
            borderLeft: "3px solid var(--everlog-status-neutral)",
            borderRadius: 4, padding: "8px 12px", marginBottom: 20,
            fontSize: 12, color: "var(--everlog-card-text)", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          }}>
            <span>
              Versão <strong>{activeVersionLabel}</strong> (histórica), somente leitura.
            </span>
            <button
              type="button"
              onClick={function() { setVersaoSelecionada(null); }}
              style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--theme-accent)", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}
            >
              voltar à versão atual
            </button>
          </div>
        )}

        {/* ── Identificação ── */}
        <SectionTitle>Identificação</SectionTitle>
        <Row>
          <EditField label="Status" locked={statusLocked} md="3">
            <Input type="select" value={form.statusEmbarque}
              onChange={set("statusEmbarque")} disabled={statusLocked}
              style={inputStyle(statusLocked)}>
              {QUOTE_STATUS_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Input>
          </EditField>
          <EditField label="Nº do Embarque" locked={dataLocked} md="3">
            <Input type="text" value={form.numEmbarque}
              onChange={set("numEmbarque")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Nº do pedido" locked={dataLocked} md="3">
            <Input type="text" value={form.codigoPedido || ""}
              maxLength={20}
              placeholder={dataLocked ? "" : "Ex.: BR25258"}
              onChange={set("codigoPedido")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Tipo de cotação" locked={true} md="3">
            <Input type="text" value={activeDoc.origemCotacao || "—"} disabled style={inputStyle(true)} />
          </EditField>
        </Row>

        {/* ── Coleta / Origem (SUS-100: antes só aparecia o CNPJ, em Nota Fiscal) ── */}
        <SectionTitle>Coleta / Origem</SectionTitle>
        <Row>
          <ReadonlyField label="Unidade" value={(unidadeOrigem && unidadeOrigem.nome) || "—"} />
          <ReadonlyField label="CNPJ" value={formatCnpj(activeDoc.cnpjRemetente)} />
        </Row>

        {/* ── Local de entrega (o frete é calculado por ele) ── */}
        <SectionTitle>Local de entrega</SectionTitle>
        <Row>
          <ReadonlyField label="CEP" value={formatCep(activeDoc.cepDestino)} />
          <ReadonlyField label="Cidade / UF"
            value={[activeDoc.cidadeDestino, activeDoc.ufDestino].filter(Boolean).join(" / ")} />
          <EditField label="Nome do local" locked={dataLocked}>
            <Input type="text" value={form.entregaNomeLocal || ""} onChange={set("entregaNomeLocal")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Rua" locked={dataLocked} md="6">
            <Input type="text" value={form.entregaLogradouro || ""} onChange={set("entregaLogradouro")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Número" locked={dataLocked} md="2">
            <Input type="text" value={form.entregaNumero || ""} onChange={set("entregaNumero")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Bairro" locked={dataLocked} md="4">
            <Input type="text" value={form.entregaBairro || ""} onChange={set("entregaBairro")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Complemento" locked={dataLocked} md="12">
            <Input type="text" value={form.entregaComplemento || ""} onChange={set("entregaComplemento")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
        </Row>

        {/* ── Destinatário (NF) (PO1-3487) ── */}
        <SectionTitle>Destinatário (NF)</SectionTitle>
        <Row>
          <EditField label="CNPJ/CPF" locked={dataLocked}>
            <Input id="edit-cotacao-dest-cnpj" type="text" value={form.destCnpjCpf || ""} inputMode="numeric" maxLength={18}
              onChange={(e) => { const v = formatCnpjCpf(e.target.value); setForm((prev) => ({ ...prev, destCnpjCpf: v })); setCnpjAviso(null); }}
              onBlur={buscarCnpjDestinatario} disabled={dataLocked}
              invalid={!cnpjCpfValido(form.destCnpjCpf)} style={inputStyle(dataLocked)} />
            {cnpjAviso ? <div style={{ color: "var(--warning, #ff8d72)", fontSize: 12 }}>{cnpjAviso}</div> : null}
          </EditField>
          <EditField label="Razão social / nome" locked={dataLocked} md="8">
            <Input type="text" value={form.cliente}
              onChange={set("cliente")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Endereço" locked={dataLocked} md="6">
            <Input type="text" value={form.destLogradouro || ""} onChange={set("destLogradouro")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Número" locked={dataLocked} md="2">
            <Input type="text" value={form.destNumero || ""} onChange={set("destNumero")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Bairro" locked={dataLocked} md="4">
            <Input type="text" value={form.destBairro || ""} onChange={set("destBairro")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="CEP" locked={dataLocked} md="3">
            <Input type="text" value={form.destCep || ""} onChange={set("destCep")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Cidade" locked={dataLocked} md="6">
            <Input type="text" value={form.destCidade || ""} onChange={set("destCidade")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="UF" locked={dataLocked} md="3">
            <Input type="text" value={form.destUf || ""} maxLength={2} onChange={set("destUf")} disabled={dataLocked} style={inputStyle(dataLocked)} />
          </EditField>
        </Row>

        {/* ── Nota Fiscal ── */}
        <SectionTitle>Nota Fiscal</SectionTitle>
        <Row>
          <EditField label="Valor da Mercadoria (R$)" locked={dataLocked}>
            <Input type="text" inputMode="decimal" value={form.valorNF}
              onChange={set("valorNF")} onBlur={blurCurrency("valorNF")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <ReadonlyField label="Data Embarque" value={formatDate(activeDoc.dataEmbarque)} />
        </Row>

        {/* ── Peso e Volume ── */}
        <SectionTitle>Peso e Volume</SectionTitle>
        {temCaixas && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: "var(--everlog-card-muted)", marginBottom: 6 }}>
              Cotação por volumes ({activeDoc.qtdVolumes || caixasDoc.reduce(function(acc, c) { return acc + (Number(c.quantidade) || 0); }, 0)} volume(s)).
              Peso e medidas foram calculados a partir da lista e não podem ser editados aqui; para mudar os volumes, faça uma nova cotação.
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ color: "var(--everlog-card-muted)", textTransform: "uppercase", fontSize: 10, letterSpacing: "0.5px" }}>
                  <th style={{ textAlign: "left", padding: "4px 8px" }}>#</th>
                  <th style={{ textAlign: "right", padding: "4px 8px" }}>Qtd</th>
                  <th style={{ textAlign: "right", padding: "4px 8px" }}>Peso por volume (kg)</th>
                  <th style={{ textAlign: "right", padding: "4px 8px" }}>Altura (cm)</th>
                  <th style={{ textAlign: "right", padding: "4px 8px" }}>Largura (cm)</th>
                  <th style={{ textAlign: "right", padding: "4px 8px" }}>Comprimento (cm)</th>
                </tr>
              </thead>
              <tbody>
                {caixasDoc.map(function(c, i) {
                  return (
                    <tr key={i} style={{ color: "var(--everlog-card-title)", borderTop: "1px solid var(--everlog-card-border)" }}>
                      <td style={{ padding: "6px 8px" }}>{i + 1}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{c.quantidade}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{formatNum(c.peso)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{formatNum(c.altura)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{formatNum(c.largura)}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right" }}>{formatNum(c.comprimento)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <Row>
          <EditField label="Peso Bruto (kg)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.pesoBruto}
              onChange={set("pesoBruto")} onBlur={blurNum("pesoBruto")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
          {/* PO1-2903 (H2): o rotulo dizia m3 num campo de peso. Volume tem
              campo proprio agora; pesoCubado guarda quilos (cotacoes antigas
              seguem com m3 aqui, sem correcao retroativa). */}
          <EditField label="Peso Cubado (kg)" locked={dataLocked}>
            <Input type="text" inputMode="decimal" value={form.pesoCubado}
              onChange={set("pesoCubado")} onBlur={blurNum("pesoCubado")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Volume (m³)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.volume}
              onChange={set("volume")} onBlur={blurNum("volume")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
          <EditField label="Densidade (kg/m³)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.densidade}
              onChange={set("densidade")} onBlur={blurNum("densidade")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
          <EditField label="Largura (cm)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.largura}
              onChange={set("largura")} onBlur={blurNum("largura")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
          <EditField label="Altura (cm)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.altura}
              onChange={set("altura")} onBlur={blurNum("altura")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
          <EditField label="Comprimento (cm)" locked={cargaLocked}>
            <Input type="text" inputMode="decimal" value={form.comprimento}
              onChange={set("comprimento")} onBlur={blurNum("comprimento")} disabled={cargaLocked}
              style={inputStyle(cargaLocked)} />
          </EditField>
        </Row>

        {/* ── Veículos e Simulação ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--everlog-card-muted)" }}>
            Veículos e Simulação
          </div>
          {!dataLocked && (
            <Button color="primary" size="sm" outline onClick={handleAddVeiculo} style={{ fontSize: 11, padding: "3px 10px" }}>
              + Adicionar veículo
            </Button>
          )}
        </div>
        <div style={{ borderBottom: "1px solid var(--everlog-card-border)", marginBottom: 14, marginTop: 6 }} />

        {veiculosEdit.length === 0 ? (
          <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: "4px 0 12px" }}>
            {dataLocked ? "Nenhum veículo registrado." : "Nenhum veículo adicionado. Clique em \"+ Adicionar veículo\"."}
          </p>
        ) : (
          <div style={{ overflowX: "auto", marginBottom: 12, borderRadius: 6, border: "1px solid var(--everlog-card-border)", overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, background: "transparent" }}>
              <thead>
                <tr style={{ background: "var(--everlog-cte-table-head-bg)", color: "var(--everlog-cte-table-head-text)", textAlign: "left" }}>
                  <th style={{ padding: "8px 8px", width: 30, fontWeight: 600 }}>#</th>
                  <th style={{ padding: "8px 8px", fontWeight: 600 }}>Tipo de Veículo</th>
                  <th style={{ padding: "8px 8px", width: 80, fontWeight: 600 }}>Qtd</th>
                  <th style={{ padding: "8px 8px", width: 100, fontWeight: 600 }}>Peso (kg)</th>
                  <th style={{ padding: "8px 8px", width: 120, fontWeight: 600 }}>Frete Simulado</th>
                  <th style={{ width: 32 }} />
                </tr>
              </thead>
              <tbody>
                {veiculosEdit.map(function(v, i) {
                  const currentType = opcoesPlanas(vehicleTypes).find(function(t) { return t.value === v.tipoVeiculoId; }) || null;
                  return (
                    <tr key={i} style={{ background: i % 2 === 0 ? "var(--everlog-card-surface)" : "var(--everlog-card-surface-flat)", borderTop: "1px solid var(--everlog-card-border)", color: "var(--everlog-card-text)" }}>
                      <td style={{ padding: "6px 8px" }}>{i + 1}</td>
                      <td style={{ padding: "6px 4px" }}>
                        {dataLocked ? (
                          <span>{v.tipoVeiculo || "—"}</span>
                        ) : (
                          <Select
                            options={vehicleTypes}
                            value={currentType}
                            onChange={function(opt) { handleVeiculoTypeChange(i, opt); }}
                            placeholder="Selecione..."
                            isClearable
                            styles={selectThemedStyles}
                            {...reactSelectPortalProps()}
                          />
                        )}
                      </td>
                      <td style={{ padding: "6px 4px" }}>
                        {dataLocked ? (
                          <span>{v.quantidade != null ? v.quantidade : "—"}</span>
                        ) : (
                          <Input
                            type="text" inputMode="numeric" value={v.quantidade}
                            onChange={function(e) {
                              const val = e.target.value.replace(/\D/g, "");
                              setVeiculosEdit(function(prev) {
                                return prev.map(function(x, j) { return j === i ? Object.assign({}, x, { quantidade: val }) : x; });
                              });
                            }}
                            onBlur={function() {
                              if (!v.quantidade) {
                                setVeiculosEdit(function(prev) {
                                  return prev.map(function(x, j) { return j === i ? Object.assign({}, x, { quantidade: "1" }) : x; });
                                });
                              }
                            }}
                            style={Object.assign({}, inputStyle(false), { padding: "2px 6px", height: 30 })}
                          />
                        )}
                      </td>
                      <td style={{ padding: "6px 4px" }}>
                        {dataLocked ? (
                          <span>{v.peso != null ? v.peso : "—"}</span>
                        ) : (
                          <Input
                            type="text" inputMode="decimal" value={v.peso}
                            onChange={setVeiculoField(i, "peso")}
                            placeholder={i === 0 ? (form.pesoBruto || "") : ""}
                            style={Object.assign({}, inputStyle(false), { padding: "2px 6px", height: 30 })}
                          />
                        )}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {v.freteSimulado != null ? (
                          <span style={{ color: "var(--everlog-status-success)", fontWeight: 600 }}>{formatCurrency(v.freteSimulado)}</span>
                        ) : "—"}
                      </td>
                      <td style={{ padding: "6px 4px", textAlign: "center" }}>
                        {!dataLocked && i > 0 && (
                          <button
                            onClick={function() { handleRemoveVeiculo(i); }}
                            style={{ background: "none", border: "none", color: "var(--everlog-status-danger)", cursor: "pointer", fontSize: 18, padding: "0 4px", lineHeight: 1 }}
                            title="Remover"
                          >×</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!dataLocked && (
          <div style={{ marginBottom: 8 }}>
            <Button
              color="primary" size="sm"
              onClick={handleSimular}
              disabled={simulating}
              style={{ fontSize: 12, padding: "5px 16px" }}
            >
              {simulating ? "Simulando..." : "Simular Fretes"}
            </Button>
          </div>
        )}

        {/* ── Frete e Transportadora ── */}
        <div id="cotacao-secao-frete" style={{ scrollMarginTop: 12 }}>
          <SectionTitle>Frete e Transportadora</SectionTitle>
        </div>
        {/* PO1-3200: o campo avulso "Frete Spot (R$)" saiu da tela. O valor
            negociado entra pelo "+ Frete Spot" das propostas e vai para
            Valor Total Frete; `freteSpot` continua no payload com o valor já
            gravado, só não é mais editável aqui. */}
        <Row>
          <EditField label="Transportadora" md="6" locked={dataLocked}>
            <Input type="text" value={form.transportadora}
              onChange={set("transportadora")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
          <EditField label="Valor Total Frete (R$)" md="6" locked={dataLocked}>
            <Input type="text" inputMode="decimal" value={form.valor_total}
              onChange={set("valor_total")} onBlur={blurCurrency("valor_total")} disabled={dataLocked}
              style={inputStyle(dataLocked)} />
          </EditField>
        </Row>

        {/* ── Serviços (PO1-2769) ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.5px", color: "var(--everlog-card-title)",
          }}>
            Serviços
          </div>
          {!dataLocked && (
            <Button
              color="primary" size="sm" outline
              onClick={handleAddServico}
              style={{ fontSize: 11, padding: "3px 10px" }}
            >
              + Adicionar serviço
            </Button>
          )}
        </div>
        <div style={{ borderBottom: "1px solid var(--everlog-card-text-softer)", marginBottom: 14, marginTop: 6 }} />

        {servicosEdit.length === 0 ? (
          <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: "4px 0 8px" }}>
            Nenhum serviço adicionado. Os valores informados aqui somam ao frete.
          </p>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {servicosEdit.map(function(s, i) {
                return (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 2, minWidth: 0 }}>
                      {dataLocked ? (
                        <span style={{ fontSize: 13, color: "var(--everlog-card-text)" }}>{s.tipoServico || "—"}</span>
                      ) : (
                        <Select
                          options={tiposServico}
                          value={s.idTipoServico != null ? { value: s.idTipoServico, label: s.tipoServico } : null}
                          onChange={function(opt) { handleServicoTipoChange(i, opt); }}
                          placeholder="Tipo de serviço..."
                          isClearable
                          styles={selectThemedStyles}
                          {...reactSelectPortalProps()}
                        />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      {dataLocked ? (
                        <span style={{ fontSize: 13, color: "var(--everlog-card-text)" }}>{s.valor || "—"}</span>
                      ) : (
                        <Input
                          type="text" inputMode="decimal" value={s.valor}
                          onChange={setServicoValor(i)} onBlur={blurServicoValor(i)}
                          placeholder="R$ 0,00"
                          style={inputStyle(false)}
                        />
                      )}
                    </div>
                    <div style={{ width: 24, textAlign: "center" }}>
                      {!dataLocked && (
                        <button
                          onClick={function() { handleRemoveServico(i); }}
                          style={{ background: "none", border: "none", color: "var(--everlog-status-danger)", cursor: "pointer", fontSize: 18, padding: "0 4px", lineHeight: 1 }}
                          title="Remover serviço"
                        >×</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, fontSize: 13, marginBottom: 8 }}>
              <span style={{ color: "var(--everlog-card-muted)" }}>Total (frete + serviços):</span>
              <strong style={{ color: "var(--everlog-card-title)" }}>{formatCurrency(totalComServicos)}</strong>
            </div>
          </>
        )}

        {/* ── Propostas simuladas ── */}
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20 }}>
            <div style={{
              fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.5px", color: "var(--everlog-card-muted)",
            }}>
              Propostas Simuladas
            </div>
            {!dataLocked && !spotAberto && (
              <Button
                color="primary" size="sm" outline
                onClick={function() { setSpotAberto(true); }}
                title="Incluir transportadora da carteira com valor negociado"
                style={{ fontSize: 11, padding: "3px 10px" }}
              >
                + Frete Spot
              </Button>
            )}
          </div>
          <div style={{ borderBottom: "1px solid var(--everlog-card-border)", marginBottom: 14, marginTop: 6 }} />

          {!dataLocked && spotAberto && (
            <FreteSpotForm
              idEmbarcador={emb.idEmbarcador || empresaId}
              onAdd={handleAddSpot}
              onCancel={function() { setSpotAberto(false); }}
            />
          )}

          {localProposals.length === 0 ? (
            <p style={{ color: "var(--everlog-card-text-softer)", fontSize: 12, margin: "4px 0 8px" }}>
              Nenhuma proposta disponível. Clique em "Re-simular Fretes" para buscar.
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
              {localProposals.map(function(c, i) {
                var isSelected = mesmaProposta(form, c);
                return (
                  <div key={i} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "6px 10px", borderRadius: 4, fontSize: 12,
                    background: isSelected
                      ? "color-mix(in srgb, var(--everlog-status-info) 14%, transparent)"
                      : "var(--everlog-card-surface-pill)",
                    color: isSelected ? "var(--everlog-status-info)" : "var(--everlog-card-text)",
                    border: isSelected
                      ? "1px solid color-mix(in srgb, var(--everlog-status-info) 30%, transparent)"
                      : "1px solid transparent",
                  }}>
                    <span>
                      {i === 0 && <span style={{ color: "var(--everlog-status-warning)", marginRight: 4 }}>★</span>}
                      {c.transportadora}
                      {c.fonte ? <FonteBadge fonte={c.fonte} /> : null}
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(c.valor)}</span>
                      <span style={{ fontWeight: 600, marginLeft: 16 }}>{c.prazo != null ? `${c.prazo}d` : "—"}</span>
                      {!dataLocked && !isSelected && (
                        <button
                          onClick={function() { handleSelectProposal(c); }}
                          style={{
                            background: "var(--everlog-info-button-bg)",
                            border: "1px solid var(--everlog-info-button-border)",
                            color: "var(--everlog-info-button-text)",
                            borderRadius: 4, padding: "2px 8px",
                            fontSize: 11, cursor: "pointer",
                          }}
                        >
                          Selecionar
                        </button>
                      )}
                      {isSelected && (
                        <span style={{ fontSize: 11, color: "var(--everlog-status-info)", fontWeight: 600 }}>✓ Selecionada</span>
                      )}
                      {!dataLocked && c.fonte === "spot" && (
                        <button
                          onClick={function() { handleRemoveSpot(c); }}
                          title="Remover frete spot"
                          style={{
                            background: "transparent", border: "none",
                            color: "var(--everlog-card-muted)",
                            padding: "0 4px", fontSize: 12, cursor: "pointer",
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>

        {/* ── Coleta e entrega (PO1-3481) ── */}
        <SectionTitle>Coleta e entrega</SectionTitle>
        <Row>
          <EditField label="Coletar até" locked={dataLocked}>
            <Input id="edit-cotacao-coletar-ate" type="datetime-local" value={form.coletarAte || ""}
              onChange={set("coletarAte")} disabled={dataLocked}
              invalid={!!limitesErros.coletarAte} style={inputStyle(dataLocked)} />
            {limitesErros.coletarAte ? (
              <div style={{ color: "var(--danger, #ec250d)", fontSize: 12 }}>{limitesErros.coletarAte}</div>
            ) : ehUrgente(form.coletarAte) ? (
              <div style={{ color: "var(--warning, #ff8d72)", fontSize: 12, fontWeight: 600 }}>Urgente: coleta nas próximas 24 horas.</div>
            ) : null}
          </EditField>
          <EditField label="Entregar até" locked={dataLocked}>
            <Input id="edit-cotacao-entregar-ate" type="datetime-local" value={form.entregarAte || ""}
              onChange={set("entregarAte")} disabled={dataLocked}
              invalid={!!limitesErros.entregarAte} style={inputStyle(dataLocked)} />
            {limitesErros.entregarAte ? (
              <div style={{ color: "var(--danger, #ec250d)", fontSize: 12 }}>{limitesErros.entregarAte}</div>
            ) : null}
          </EditField>
        </Row>

        {/* ── Observações ── */}
        <SectionTitle>Observações</SectionTitle>
        <FormGroup>
          <Input type="textarea" rows={3} value={form.observacoes}
            onChange={set("observacoes")} disabled={dataLocked}
            placeholder={dataLocked ? "" : "Observações..."}
            style={{ ...inputStyle(dataLocked), resize: "vertical" }} />
        </FormGroup>

        {/* ── Versões (PO1-2783) ── */}
        {embHasVersions && (
          <>
            <SectionTitle>Versões</SectionTitle>
            <VersoesTimeline
              versoes={versoes}
              loading={versoesLoading}
              activeId={activeDoc._id}
              onSelect={handleSelectVersao}
            />
          </>
        )}

        {/* ── Histórico ── */}
        <SectionTitle>Histórico de alterações</SectionTitle>
        <HistoricoTimeline historico={activeDoc.historico} />

      </ModalBody>

      <ModalFooter style={{ borderTop: "1px solid var(--everlog-card-border)", gap: 8, padding: "16px 20px" }}>
        {onDelete && !readOnly && (
          <div style={{ marginRight: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            {!confirmDelete ? (
              <Button
                color="danger"
                outline
                size="sm"
                onClick={function() { setConfirmDelete(true); }}
                disabled={saving}
              >
                Excluir
              </Button>
            ) : (
              <>
                <span style={{ fontSize: 12, color: "var(--everlog-status-danger)" }}>
                  Excluir definitivamente?
                </span>
                <Button
                  color="danger"
                  size="sm"
                  onClick={function() { onDelete(emb._id); }}
                  disabled={saving}
                >
                  {saving ? "Excluindo..." : "Sim, excluir"}
                </Button>
                <Button
                  color="secondary"
                  outline
                  size="sm"
                  onClick={function() { setConfirmDelete(false); }}
                  disabled={saving}
                >
                  Não
                </Button>
              </>
            )}
          </div>
        )}
        {canCreateVersion(currentStatus) && !readOnly && (
          <Button
            color="info"
            size="sm"
            style={{ marginRight: "auto" }}
            onClick={function() { onCreateVersion(emb); }}
            disabled={saving}
          >
            + Criar nova versão
          </Button>
        )}
        {onLeiloar && !readOnly && (leilaoAtivo || (currentStatus === "Pendente" || currentStatus === "Stand-by")) && (
          <Button
            color="success"
            outline
            size="sm"
            onClick={function() { onLeiloar(emb, leilaoAtivo || null); }}
            disabled={saving}
            title={leilaoAtivo ? `Leilão reverso nº ${leilaoAtivo.numero}` : "Abrir um leilão reverso de frete a partir desta cotação"}
          >
            {leilaoAtivo ? `Ver leilão nº ${leilaoAtivo.numero}` : "Leiloar"}
          </Button>
        )}
        <Button className="lc-btn-cancel" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {canFlex && (
          <Button color={flexAction.color} onClick={handleFlexAction} disabled={saving}>
            {saving ? "Processando..." : flexAction.label}
          </Button>
        )}
        {!isRealizado && !readOnly && (
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default EditCotacaoModal;
